import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/features/sales';
import { cn } from '@/lib/utils';

import {
  type DepositStatus,
  depositStatusLabels,
} from '@borradh-workspace/api-client/types';

/** The trimmed deposit shape carried on appointment read models / event metadata. */
export interface DepositSummary {
  status: DepositStatus;
  amountCents: number | null;
  currency: string;
  paidAt: string | null;
  refundedAmountCents?: number;
  refundPendingAmountCents?: number;
  forfeitedAmountCents?: number;
  isGroupAllocation?: true;
  reconciliationRequired?: true;
}

/** Read a deposit summary off an arbitrary metadata bag (IEvent.metadata). */
export function readDepositFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): DepositSummary | null {
  const raw = metadata?.deposit;
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<DepositSummary>;
  if (
    typeof d.status !== 'string' ||
    (typeof d.amountCents !== 'number' && d.amountCents !== null)
  ) {
    return null;
  }
  return {
    status: d.status as DepositStatus,
    amountCents: d.amountCents,
    currency: typeof d.currency === 'string' ? d.currency : 'eur',
    paidAt: typeof d.paidAt === 'string' ? d.paidAt : null,
    ...(typeof d.refundedAmountCents === 'number'
      ? { refundedAmountCents: d.refundedAmountCents }
      : {}),
    ...(typeof d.refundPendingAmountCents === 'number'
      ? { refundPendingAmountCents: d.refundPendingAmountCents }
      : {}),
    ...(typeof d.forfeitedAmountCents === 'number'
      ? { forfeitedAmountCents: d.forfeitedAmountCents }
      : {}),
    ...(d.isGroupAllocation === true
      ? { isGroupAllocation: true as const }
      : {}),
    ...(d.amountCents === null || d.reconciliationRequired === true
      ? { reconciliationRequired: true as const }
      : {}),
  };
}

const toneClasses: Record<DepositStatus, string> = {
  paid: 'border-transparent bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  pending:
    'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  expired: 'border-transparent bg-muted text-muted-foreground',
  cancelled:
    'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  refunded: 'border-transparent bg-muted text-muted-foreground',
  // A STAFF-marked late cancel or no-show that forfeited the deposit — the
  // clinic keeps it, so this is the same "closed, no money moving" tone as
  // cancelled, not a refund.
  forfeited:
    'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  revoked: 'border-transparent bg-muted text-muted-foreground',
  failed:
    'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  // Money captured, booking NOT confirmed — staff must act.
  needs_review:
    'border-transparent bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
};

const unknownTone = 'border-transparent bg-muted text-muted-foreground';

/**
 * "paid", "link not created", … for any deposit status — including one this
 * build does not know. A status added by a later release (read here after a
 * rollback, or by a tab still running this build) reads as its own words
 * instead of crashing the booking panel on an undefined label.
 */
export function depositStatusText(status: string): string {
  const label = (depositStatusLabels as Record<string, string | undefined>)[
    status
  ];
  return (label ?? status.replace(/_/g, ' ')).toLowerCase();
}

/**
 * A badge for an appointment's deposit state, e.g. "Deposit paid" / "Deposit
 * pending". Colour matches the deposit status. Renders nothing when there is no
 * deposit.
 */
export function DepositBadge({
  deposit,
  className,
}: {
  deposit: DepositSummary | null;
  className?: string;
}) {
  if (!deposit) return null;
  const unknown =
    deposit.amountCents === null || deposit.reconciliationRequired === true;
  return (
    <Badge
      className={cn(
        'shrink-0',
        (toneClasses as Record<string, string | undefined>)[
          unknown ? 'needs_review' : deposit.status
        ] ?? unknownTone,
        className
      )}
    >
      {unknown
        ? 'Payment needs reconciliation'
        : `Deposit ${depositStatusText(deposit.status)}`}
    </Badge>
  );
}

/**
 * A "Deposit paid · €X" line for the appointment detail body. Renders the amount
 * with a status-appropriate verb. Returns null when there is no deposit.
 */
export function DepositDetailRow({
  deposit,
}: { deposit: DepositSummary | null }) {
  if (!deposit) return null;
  const unknown =
    deposit.amountCents === null || deposit.reconciliationRequired === true;
  const amount = unknown
    ? 'Reconcile payment'
    : formatMoney(deposit.amountCents as number, deposit.currency);
  const label = unknown
    ? 'Payment amount unknown'
    : deposit.status === 'paid'
      ? 'Deposit paid'
      : deposit.status === 'pending'
        ? 'Deposit due'
        : `Deposit ${depositStatusText(deposit.status)}`;
  return (
    <div className="flex items-center justify-between rounded-lg border bg-card p-3">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{amount}</span>
    </div>
  );
}
